import type { Variants } from 'framer-motion';

import { DURATIONS, EASINGS, TRANSITIONS } from './constants';

let reducedMotion: boolean | null = null;
export const prefersReducedMotion = (): boolean => {
  if (reducedMotion === null) {
    reducedMotion =
      typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
  }
  return reducedMotion;
};

export const reducedMotionVariant = (variants: Variants): Variants =>
  Object.fromEntries(
    Object.entries(variants).map(([key, value]) => [
      key,
      {
        ...(typeof value === 'object' && value !== null ? value : {}),
        transition: { duration: 0.01 },
      },
    ])
  );

export const fadeInVariants: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: TRANSITIONS.default,
  },
  exit: {
    opacity: 0,
    transition: TRANSITIONS.fast,
  },
};

export const fadeInScaleVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.9,
    y: 20,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: TRANSITIONS.slow,
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    y: 20,
    transition: TRANSITIONS.fast,
  },
};

export const slideUpVariants: Variants = {
  initial: {
    opacity: 0,
    y: 40,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: DURATIONS.slower,
      ease: EASINGS.soft,
    },
  },
};

export const slideInLeftVariants: Variants = {
  initial: { x: '-100%', opacity: 0 },
  animate: {
    x: 0,
    opacity: 1,
    transition: TRANSITIONS.slow,
  },
  exit: {
    x: '-100%',
    opacity: 0,
    transition: TRANSITIONS.fast,
  },
};

export const slideInRightVariants: Variants = {
  initial: { x: '100%', opacity: 0 },
  animate: {
    x: 0,
    opacity: 1,
    transition: TRANSITIONS.slow,
  },
  exit: {
    x: '100%',
    opacity: 0,
    transition: TRANSITIONS.fast,
  },
};

export const fadeInLeftVariants: Variants = {
  initial: { x: -30, opacity: 0 },
  animate: {
    x: 0,
    opacity: 1,
    transition: TRANSITIONS.slow,
  },
};

export const fadeInRightVariants: Variants = {
  initial: { x: 30, opacity: 0 },
  animate: {
    x: 0,
    opacity: 1,
    transition: TRANSITIONS.slow,
  },
};

export const staggerContainerVariants: Variants = {
  initial: { opacity: 1 },
  animate: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

export const staggerItemVariants: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: TRANSITIONS.spring,
  },
};

export const buttonTapVariants: Variants = {
  hover: {
    scale: 1.02,
    transition: TRANSITIONS.fast,
  },
  tap: {
    scale: 0.98,
    transition: TRANSITIONS.fast,
  },
};

export const interactiveHoverVariants: Variants = {
  initial: { scale: 1 },
  hover: {
    scale: 1.05,
    y: -2,
    transition: TRANSITIONS.spring,
  },
  tap: {
    scale: 0.95,
    transition: TRANSITIONS.fast,
  },
};

export const socialIconVariants: Variants = {
  initial: { scale: 1, rotate: 0 },
  hover: {
    scale: 1.2,
    rotate: [0, -10, 10, 0],
    transition: {
      duration: 0.4,
      ease: EASINGS.soft,
    },
  },
  tap: { scale: 0.9 },
};

export const skeletonVariants: Variants = {
  animate: {
    opacity: [0.3, 0.7, 0.3],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

export const pulseVariants: Variants = {
  animate: {
    scale: [1, 1.05, 1],
    opacity: [1, 0.8, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

export const bounceVariants: Variants = {
  animate: {
    y: [0, -20, 0],
    rotate: [0, 5, -5, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

export const floatingVariants: Variants = {
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

export const arrowAnimation: Variants = {
  animate: {
    x: [0, 5, 0],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

export const accordionVariants: Variants = {
  initial: { height: 0, opacity: 0 },
  animate: {
    height: 'auto',
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: EASINGS.soft,
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: EASINGS.soft,
    },
  },
};

export const slideDownVariants: Variants = {
  initial: { opacity: 0, y: -10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: TRANSITIONS.fast,
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: TRANSITIONS.fast,
  },
};

export const cardHoverVariants: Variants = {
  hover: {
    y: -8,
    scale: 1.02,
    transition: TRANSITIONS.spring,
  },
  tap: {
    scale: 0.98,
    transition: TRANSITIONS.fast,
  },
};

export const socialButtonVariants: Variants = {
  hover: {
    scale: 1.02,
    y: -2,
    transition: TRANSITIONS.fast,
  },
  tap: {
    scale: 0.98,
    transition: TRANSITIONS.fast,
  },
};

export const listHoverVariants: Variants = {
  hover: {
    x: 5,
    transition: TRANSITIONS.fast,
  },
};

export const wiggleVariants: Variants = {
  wiggle: {
    rotate: [0, -15, 15, -10, 10, -5, 5, 0],
    scale: [1, 1.25, 1.25, 1.2, 1.2, 1.1, 1.1, 1],
    transition: {
      duration: 0.6,
      ease: 'easeInOut',
    },
  },
  default: {
    rotate: 0,
    scale: 1,
  },
};
